import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { usePage, router } from "@inertiajs/react";

// ─── Sidebar helper ─────────────────────────────────────────────────────────
export function buildSidebarSections(role, activePage) {
  return [
    {
      heading: "Navigation",
      links: [
        { label: "Atlas AI", icon: "◈", href: `/wireframes/atlas-ai`, active: activePage === "atlas-ai" },
      ],
    },
    {
      heading: "Dashboard",
      links: [
        { label: "Ops Owner", icon: "⚙", href: "/wireframes/ops/dashboard", active: activePage === "dashboard" && role === "ops" },
        { label: "Sales Channel", icon: "◎", href: "/wireframes/sales/dashboard", active: activePage === "dashboard" && role === "sales" },
        { label: "Perf. Marketing", icon: "▲", href: "/wireframes/marketing/dashboard", active: activePage === "dashboard" && role === "marketing" },
        { label: "CFO / CEO", icon: "◆", href: "/wireframes/exec/dashboard", active: activePage === "dashboard" && role === "exec" },
      ],
    },
    {
      heading: "Inventory",
      links: [
        { label: "Empty State", icon: "○", href: "/wireframes/inventory/empty", active: activePage === "inventory-empty" },
        { label: "With Data", icon: "📦", href: "/wireframes/inventory/with-data", active: activePage === "inventory-data" },
      ],
    },
    {
      heading: "Back to App",
      links: [
        { label: "Dashboard", icon: "◈", href: `/` },
      ],
    },
  ];
}

// ─── Layout ─────────────────────────────────────────────────────────────────
export default function WireframeLayout({
  children,
  title,
  subtitle,
  titleIcon,
  titleColor,
  sidebarLinks = [],
}) {
  const [col, setCol] = useState(false);
  const [time, setTime] = useState(new Date());
  const [userMenu, setUserMenu] = useState(false);
  const userBtnRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { props } = usePage<{ user?: { name: string; email: string } }>();
  const user = props.user;

  useEffect(() => { const t = setInterval(() => setTime(new Date()), 60000); return () => clearInterval(t); }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        userBtnRef.current && !userBtnRef.current.contains(target) &&
        userMenuRef.current && !userMenuRef.current.contains(target)
      ) {
        setUserMenu(false);
      }
    };
    if (userMenu) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [userMenu]);

  const handleLogout = () => {
    router.delete("/logout");
  };

  return (
    <div className="sop-platform" style={{ display: "flex", height: "100vh", fontFamily: "'Geist Sans', 'Segoe UI', system-ui, -apple-system, sans-serif", background: "var(--muted)", color: "var(--foreground)", overflow: "hidden" }}>
      <link href="https://fonts.googleapis.com/css2?family=Geist+Mono:wght@400;500&display=swap" rel="stylesheet" />

      {/* Sidebar */}
      <div style={{ width: col ? 56 : 220, background: "var(--foreground)", display: "flex", flexDirection: "column", transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)", flexShrink: 0, overflow: "hidden" }}>
        {/* Brand */}
        <div style={{ padding: col ? "16px 10px" : "16px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => setCol(!col)}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: "linear-gradient(135deg, var(--success), var(--chart-2))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: "#fff", flexShrink: 0 }}>◈</div>
          {!col && <span style={{ fontSize: 14, fontWeight: 700, color: "#fff", whiteSpace: "nowrap", letterSpacing: "-0.01em" }}>ATLAS AI</span>}
        </div>

        {/* Navigation */}
        <div style={{ padding: col ? "10px 6px" : "10px 10px", flex: 1 }}>
          {sidebarLinks.map((section, si) => (
            <div key={si}>
              {!col && section.heading && (
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 1.5, padding: "6px 8px 4px", marginBottom: 2, marginTop: si > 0 ? 10 : 0, fontWeight: 600 }}>{section.heading}</div>
              )}
              {section.links.map(link => (
                <a key={link.href} href={link.href} style={{ width: "100%", display: "flex", alignItems: "center", gap: 9, padding: col ? "8px 0" : "8px 10px", marginBottom: 1, background: link.active ? "rgba(255,255,255,0.08)" : "transparent", border: link.active ? "1px solid rgba(255,255,255,0.1)" : "1px solid transparent", borderRadius: 8, cursor: "pointer", color: link.active ? "#fff" : "var(--muted-foreground)", transition: "all 0.15s", justifyContent: col ? "center" : "flex-start", fontFamily: "inherit", fontSize: 13, textDecoration: "none" }}>
                  <span style={{ fontSize: 15, width: 18, textAlign: "center", flexShrink: 0, color: link.active ? "var(--success)" : "var(--muted-foreground)" }}>{link.icon}</span>
                  {!col && <span style={{ fontWeight: link.active ? 600 : 400, whiteSpace: "nowrap" }}>{link.label}</span>}
                </a>
              ))}
            </div>
          ))}
        </div>

        {/* User */}
        <div ref={userBtnRef} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <button
            onClick={() => setUserMenu(!userMenu)}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: col ? "12px 0" : "12px 16px", background: userMenu ? "rgba(255,255,255,0.06)" : "transparent", border: "none", cursor: "pointer", transition: "background 0.15s", justifyContent: col ? "center" : "flex-start", fontFamily: "inherit" }}
          >
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "linear-gradient(135deg, var(--chart-2), #8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, color: "#fff", flexShrink: 0 }}>
              {user?.name ? user.name.charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase() || "?"}
            </div>
            {!col && user && (
              <>
                <div style={{ flex: 1, textAlign: "left", overflow: "hidden" }}>
                  <div style={{ fontSize: 13, color: "#fff", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user.name || user.email}</div>
                  {user.name && <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user.email}</div>}
                </div>
                <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", flexShrink: 0, transition: "transform 0.15s", transform: userMenu ? "rotate(90deg)" : "none" }}>▸</span>
              </>
            )}
          </button>

          {userMenu && createPortal(
            <div ref={userMenuRef} style={{ position: "fixed", bottom: 12, left: col ? 60 : 224, background: "#fff", borderRadius: 10, boxShadow: "0 4px 24px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)", minWidth: 200, padding: "6px", zIndex: 9999 }}>
              {user && (
                <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--muted)" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>{user.name || user.email}</div>
                  {user.name && <div style={{ fontSize: 11, color: "var(--muted-foreground)", marginTop: 2 }}>{user.email}</div>}
                </div>
              )}
              <button
                onClick={handleLogout}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", background: "transparent", border: "none", borderRadius: 6, cursor: "pointer", color: "var(--destructive)", fontSize: 13, fontFamily: "inherit", transition: "background 0.12s" }}
                onMouseEnter={e => (e.currentTarget.style.background = "var(--destructive-foreground)")}
                onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
              >
                <span style={{ fontSize: 14 }}>⏻</span>
                Logout
              </button>
            </div>,
            document.body
          )}
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "12px 24px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, background: "#fff" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {titleIcon && <span style={{ fontSize: 17, color: titleColor }}>{titleIcon}</span>}
              <h1 style={{ fontSize: 16, fontWeight: 700, color: "var(--foreground)", margin: 0, letterSpacing: "-0.02em" }}>{title}</h1>
            </div>
            {subtitle && <div style={{ fontSize: 12, color: "var(--muted-foreground)", marginTop: 1, marginLeft: titleIcon ? 25 : 0 }}>{subtitle}</div>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>{time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · Mar 1, 2025</span>
            <div style={{ display: "flex", gap: 6 }}>
              {["🔔", "⚙"].map((ic, i) => (
                <div key={i} style={{ width: 32, height: 32, borderRadius: 8, background: "var(--muted)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, cursor: "pointer" }}>{ic}</div>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: "auto", padding: "20px 24px" }}>
          {children}
        </div>
      </div>

      <style>{`
        .sop-platform ::-webkit-scrollbar { width: 6px; }
        .sop-platform ::-webkit-scrollbar-track { background: transparent; }
        .sop-platform ::-webkit-scrollbar-thumb { background: var(--chart-muted); border-radius: 3px; }
        .sop-platform ::-webkit-scrollbar-thumb:hover { background: var(--muted-foreground); }
        .sop-platform button { font-family: inherit; }
      `}</style>
    </div>
  );
}
